import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { offersAPI, productsAPI, adminAPI, quotesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { usePagination } from '../components/Pagination';

import { OfferStatsCards } from '../components/admin/OfferStatsCards';
import { OffersTable } from '../components/admin/OffersTable';
import { CreateOfferDialog } from '../components/admin/CreateOfferDialog';
import { DeleteOfferDialog } from '../components/admin/DeleteOfferDialog';

export const AdminOffers = () => {
    const { user } = useAuth();
    const isSuperAdmin = user?.is_super_admin === true;
    const [offers, setOffers] = useState([]);
    const [products, setProducts] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [searchProduct, setSearchProduct] = useState('');
    const [searchClient, setSearchClient] = useState('');
    const [quoteNumber, setQuoteNumber] = useState('');
    const [isLoadingQuote, setIsLoadingQuote] = useState(false);
    const [importedQuoteNumber, setImportedQuoteNumber] = useState('');

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        products: [],
        target_client_ids: [],
        expires_at: ''
    });

    const fetchData = async () => {
        try {
            const [offersRes, productsRes, clientsRes] = await Promise.all([
                offersAPI.list(),
                productsAPI.getAll(),
                adminAPI.getClients({ limit: 10000 })
            ]);
            setOffers(offersRes.data.offers || []);
            setProducts(productsRes.data.products || []);
            setClients(clientsRes.data.clients || []);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const resetForm = () => {
        setFormData({ title: '', description: '', products: [], target_client_ids: [], expires_at: '' });
        setSelectedOffer(null);
        setQuoteNumber('');
        setImportedQuoteNumber('');
    };

    const handleImportQuote = async () => {
        if (!quoteNumber.trim()) return;
        setIsLoadingQuote(true);
        try {
            const res = await quotesAPI.getByNumber(quoteNumber.trim());
            const quote = res.data;
            const quoteProducts = quote.items.map(item => {
                const match = products.find(p => p.id === item.product_id);
                return {
                    product_id: item.product_id,
                    product_name: item.product_name,
                    price: 0,
                    unit: item.unit || match?.unit || 'kg',
                    image_url: match?.image_url || '',
                };
            });
            const client = clients.find(c => c.id === quote.user_id);
            setFormData(prev => ({
                ...prev,
                title: prev.title || `Offre suite demande ${quote.quote_number}`,
                description: prev.description || `Reponse a la demande ${quote.quote_number} de ${quote.company_name}`,
                products: quoteProducts,
                target_client_ids: client ? [client.id] : prev.target_client_ids,
            }));
            setImportedQuoteNumber(quote.quote_number);
            toast.success(`Demande ${quote.quote_number} importee : ${quoteProducts.length} produit(s), client: ${quote.company_name}. Remplissez les prix !`);
        } catch {
            toast.error('Demande non trouvee. Verifiez le numero.');
        } finally {
            setIsLoadingQuote(false);
        }
    };

    const handleCreateOffer = async (e) => {
        e.preventDefault();
        if (!formData.title) { toast.error('Veuillez saisir un titre'); return; }
        if (formData.products.length === 0) { toast.error('Veuillez selectionner au moins un produit'); return; }
        if (formData.target_client_ids.length === 0) { toast.error('Veuillez selectionner au moins un client'); return; }

        try {
            setIsCreating(true);
            const res = await offersAPI.create(formData);
            const nb = res.data?.nb_clients || 0;
            toast.success(`Offre creee ! ${nb} client(s) notifie(s). Les emails sont en cours d'envoi.`);
            setIsCreateDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la creation');
        } finally {
            setIsCreating(false);
        }
    };

    const handleToggleActive = async (offer) => {
        try {
            await offersAPI.update(offer.id, { is_active: !offer.is_active });
            toast.success(offer.is_active ? 'Offre desactivee' : 'Offre activee');
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la mise a jour');
        }
    };

    const handleDelete = async () => {
        try {
            await offersAPI.delete(selectedOffer.id);
            toast.success('Offre supprimee');
            setIsDeleteDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    const toggleSelectAll = (pageIds) => {
        const allIn = pageIds.every(id => selectedIds.includes(id));
        setSelectedIds(prev => allIn ? prev.filter(x => !pageIds.includes(x)) : [...new Set([...prev, ...pageIds])]);
    };
    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) {
            toast.error('Aucune offre selectionnee');
            return;
        }
        if (!window.confirm(`Etes-vous sur de vouloir supprimer ${selectedIds.length} offre(s) ? Cette action est irreversible.`)) {
            return;
        }
        setBulkDeleting(true);
        try {
            const res = await offersAPI.bulkDelete({ ids: selectedIds });
            toast.success(res.data.message || `${selectedIds.length} offre(s) supprimee(s)`);
            setSelectedIds([]);
            await loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setBulkDeleting(false);
        }
    };
    const handleBulkDeleteAll = async () => {
        if (!window.confirm('SUPPRIMER TOUTES LES OFFRES ? Cette action est irreversible.')) {
            return;
        }
        setBulkDeleting(true);
        try {
            const res = await offersAPI.bulkDelete({ all: true });
            toast.success(res.data.message || 'Toutes les offres supprimees');
            setSelectedIds([]);
            await loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleCancelCreate = () => {
        if (formData.products.length > 0 || formData.title) {
            if (window.confirm('Vous avez des donnees non enregistrees. Fermer quand meme ?')) {
                setIsCreateDialogOpen(false);
                resetForm();
            }
        } else {
            setIsCreateDialogOpen(false);
        }
    };

    const { paginatedItems: paginatedOffers, currentPage, totalPages, totalItems, setCurrentPage } = usePagination(offers);

    if (loading) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jungle"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand" data-testid="admin-offers-page">
            <div className="max-w-7xl mx-auto p-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight">
                            Offres Personnalisees
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Creez des offres specifiques pour vos clients
                        </p>
                    </div>
                    <Button
                        onClick={() => { resetForm(); setIsCreateDialogOpen(true); }}
                        className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl"
                        data-testid="create-offer-button"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Creer une offre
                    </Button>
                </div>

                <OfferStatsCards offers={offers} clients={clients} />

                {isSuperAdmin && (
                    <div className="flex items-center justify-between gap-2 my-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="text-sm text-red-700">
                            {selectedIds.length > 0
                                ? `${selectedIds.length} offre(s) selectionnee(s)`
                                : 'Selectionnez des offres pour les supprimer en lot'}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleBulkDelete}
                                disabled={selectedIds.length === 0 || bulkDeleting}
                                className="border-red-300 text-red-700 hover:bg-red-100"
                                data-testid="bulk-delete-offers"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Supprimer la selection
                            </Button>
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleBulkDeleteAll}
                                disabled={bulkDeleting || offers.length === 0}
                                data-testid="bulk-delete-all-offers"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Tout supprimer
                            </Button>
                        </div>
                    </div>
                )}

                <OffersTable
                    paginatedOffers={paginatedOffers}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    setCurrentPage={setCurrentPage}
                    onCreateNew={() => { resetForm(); setIsCreateDialogOpen(true); }}
                    onToggleActive={handleToggleActive}
                    onDelete={(offer) => { setSelectedOffer(offer); setIsDeleteDialogOpen(true); }}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                    onToggleSelectAll={toggleSelectAll}
                    canBulkDelete={isSuperAdmin}
                />
            </div>

            <CreateOfferDialog
                open={isCreateDialogOpen}
                onOpenChange={setIsCreateDialogOpen}
                formData={formData}
                setFormData={setFormData}
                products={products}
                clients={clients}
                searchProduct={searchProduct}
                setSearchProduct={setSearchProduct}
                searchClient={searchClient}
                setSearchClient={setSearchClient}
                quoteNumber={quoteNumber}
                setQuoteNumber={setQuoteNumber}
                isLoadingQuote={isLoadingQuote}
                importedQuoteNumber={importedQuoteNumber}
                isCreating={isCreating}
                onSubmit={handleCreateOffer}
                onImportQuote={handleImportQuote}
                onCancel={handleCancelCreate}
            />

            <DeleteOfferDialog
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
                offer={selectedOffer}
                onDelete={handleDelete}
            />
        </div>
    );
};
